import { Badge, StateBadge } from '../../../ui/Badge';
import { Alert } from '../../../ui/feedback';
import { Markdown } from '../../../ui/Markdown';
import { ActorName, Card, KeyValue } from '../../../ui/misc';
import { Timestamp } from '../../../ui/Timestamp';
import type { AttemptDetail } from '../types';

export function ReportedResult({ a }: { a: AttemptDetail }) {
  const b = a.binding;
  return (
    <Card
      title="Reported result"
      subtitle={
        a.report ? (
          <>
            Received <Timestamp value={a.report.reportedAt} /> from <ActorName actor={a.report.reportedBy} />
          </>
        ) : (
          'No report yet.'
        )
      }
    >
      {a.report && (
        <>
          <KeyValue
            items={[
              ['Outcome', <StateBadge key="o" state={a.state} />],
              [
                'Revision',
                <span key="r" className="mono">
                  {a.report.revision ?? '—'}
                </span>,
              ],
              [
                'Execution reference',
                a.report.deploymentReference ? (
                  <a href={a.report.deploymentReference} target="_blank" rel="noopener noreferrer nofollow">
                    {a.report.deploymentReference}
                  </a>
                ) : (
                  '—'
                ),
              ],
              ['Started (supplied)', <Timestamp key="s" value={a.report.startedAt} />],
              ['Finished (supplied)', <Timestamp key="f" value={a.report.finishedAt} />],
            ]}
          />
          <span className="small strong">Verification checks</span>
          <ul className="checklist">
            {b.verificationChecks.map((c) => {
              const r = a.report!.checks.find((x) => x.name === c);
              return (
                <li key={c}>
                  {r ? (
                    <Badge tone={r.passed ? 'success' : 'error'}>{r.passed ? 'Passed' : 'Failed'}</Badge>
                  ) : (
                    <Badge tone="warning">No result</Badge>
                  )}{' '}
                  {c}
                  {a.manualChecks.includes(c) && <span className="small muted"> (manual)</span>}
                  {r?.observation && <span className="small muted"> — {r.observation}</span>}
                </li>
              );
            })}
          </ul>
          {a.report.problems.length > 0 && (
            <Alert tone="warning" title="Problems recorded">
              <ul>
                {a.report.problems.map((p, i) => (
                  <li key={i}>{p.message}</li>
                ))}
              </ul>
            </Alert>
          )}
          {a.report.partialEffects && (
            <>
              <span className="small strong">Partial effects</span>
              <Markdown>{a.report.partialEffects}</Markdown>
            </>
          )}
          {a.report.errors && (
            <>
              <span className="small strong">Errors</span>
              <Markdown>{a.report.errors}</Markdown>
            </>
          )}
          {a.report.recoveryActions && (
            <>
              <span className="small strong">Remaining recovery actions</span>
              <Markdown>{a.report.recoveryActions}</Markdown>
            </>
          )}
          {a.report.log && (
            <>
              <span className="small strong">Log</span>
              <pre className="code" style={{ maxHeight: 320, overflow: 'auto' }}>
                {a.report.log}
              </pre>
            </>
          )}
        </>
      )}
    </Card>
  );
}
